import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { formatCurrency } from '../../Types/currencyConverter';
import { formatDate } from '../../Types/formatDate';

const currencyMap: { [key: string]: string } = {
    'en-US': 'USD',
    'fr-FR': 'EUR',
    'sv-SE': 'SEK',
    'sv': 'SEK', // Add short code for Swedish
    // Add other mappings as needed
};

const getCurrencyFromLocale = (locale: string) => {
    const normalizedLocale = locale.split('-')[0]; // Get the language part of the locale
    return currencyMap[locale] || currencyMap[normalizedLocale] || 'USD';
};

const PaymentSuccess: React.FC = () => {
    const location = useLocation();

     // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>({});
    const [userLocale, setUserLocale] = useState('en-US');
     // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userCurrency, setUserCurrency] = useState('USD');

    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/SEK');
                if (!response.ok) {
                    throw new Error('Failed to fetch exchange rates');
                }
                const data = await response.json();
                setExchangeRates(data.rates);
            } catch (error) {
                console.error('Error fetching exchange rates:', error);
            }
        };

        fetchExchangeRates();
    }, []);

    useEffect(() => {
        // Set user locale and currency based on browser settings or user preferences
        const locale = navigator.language || 'en-US';
        setUserLocale(locale);
        setUserCurrency(getCurrencyFromLocale(locale));
    }, []);

    const paymentStatus = new URLSearchParams(location.search).get("status");
    const orderSummary = location.state?.orderSummary || [];
    const receiptUrl = location.state?.receiptUrl || '';
    const isBusinessUser = location.state?.isBusinessUser || false; // Check if the user is a business user

    console.log('isBusinessUser:', isBusinessUser); // Log to verify the state

    // Business customers are invoiced at 20% off the gross price (matches
    // BookingService.cs's `itemTotalPrice *= 0.8m` applied at booking
    // confirmation, and the price shown in the confirmation email/invoice).
    // This confirmation page must show that SAME net price, not the gross
    // cart price, so it never contradicts the email/invoice total.
    const BUSINESS_DISCOUNT_MULTIPLIER = 0.8;
    const getDisplayPrice = (price: number) =>
        isBusinessUser ? price * BUSINESS_DISCOUNT_MULTIPLIER : price;

    // Calculate the total price of the entire booking
    const totalBookingPrice = orderSummary.reduce((total: number, item: any) => total + getDisplayPrice(item.totalPrice), 0);

    return (
        <div className='payment-success-section py-4 pb-5 mb-5'>
            <div className='order-confirmation'>
                {paymentStatus === "success" ? (
                    <>
                        {isBusinessUser ? (
                            <>
                                <h1>Thank you for your booking. Your booking will be reviewed.</h1>
                                <div>You will be invoiced by Rajamaa AB.</div>
                            </>
                        ) : (
                            <>
                                <h1>Thank you for your order. Your transaction was successful</h1>
                                <div>You will get a confirmation email shortly.</div>
                                {receiptUrl && (
                                    <div>
                                        You can view your receipt <a href={receiptUrl} target="_blank" rel="noopener noreferrer">here</a>.
                                    </div>
                                )}
                            </>
                        )}
                        <h2>Order Summary</h2>
                        <div className={`card-container ${orderSummary.length === 1 ? 'justify-content-center' : ''}`}>
                            {orderSummary.map((item: any, index: number) => (
                                <div className='col-md-4' key={index}>
                                    <div className='card mb-4'>
                                        <img src={item.mainPictureUrl} className='card-img-top' alt={item.packageName} />
                                        <div className='card-body'>
                                            <h5 className='card-title'> <strong>Package</strong> {item.packageName}</h5>
                                            <div className='card-text'><strong>Arrival Date:</strong> {formatDate(item.arrivalDate, userLocale)}</div>
                                            <div className='card-text'><strong>Departure Date:</strong> {formatDate(item.departureDate, userLocale)}</div>
                                            <div className='card-text'><strong>Price per person:</strong> {formatCurrency(getDisplayPrice(item.pricePerNight))}</div>
                                            <div className='card-text'><strong>Total Price:</strong> {formatCurrency(getDisplayPrice(item.totalPrice))}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='total-booking-price'>
                            <h5>Total Booking Price: {formatCurrency(totalBookingPrice)}</h5>
                            {isBusinessUser ? (
                                <p>Net price (20% business discount applied, VAT excluded). This matches your invoice.</p>
                            ) : (
                                <p>Taxes included (if applicable).</p>
                            )}
                        </div>
                    </>
                ) : (
                    <h1>Payment Failed. Something went wrong. Please try again.</h1>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;